import { useState } from "react";
import { useAdminApplication, useAdminSession } from "../../app/providers";
import { Alert, Button, DetailList, Field } from "../../ui";
import type { Schemas } from "../workspace/api.generated";

/** An explicit audited read. The revealed account stays only in this open dialog. */
export function RecipientAccountReveal({
  transactionId,
}: {
  readonly transactionId: number;
}) {
  const { apiClient } = useAdminApplication();
  const session = useAdminSession();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(false);
  const [account, setAccount] =
    useState<Schemas["SensitiveRecipientAccountView"]>();
  const allowed = session.authSession?.authorities?.includes(
    "TRANSACTION_ACCOUNT_READ",
  );
  if (!allowed) return <p>계좌 원문은 별도 열람 권한이 필요합니다.</p>;
  const reveal = async () => {
    if (pending || reason.trim().length < 5 || reason.trim().length > 2000)
      return;
    setPending(true);
    setError(false);
    try {
      const response = await apiClient.post<
        Schemas["SensitiveRecipientAccountView"]
      >(
        `/api/v1/admin/bo/transactions/${transactionId}/recipient-account:reveal`,
        { reason: reason.trim() },
        { cache: "no-store" },
      );
      setAccount(response.data);
    } catch {
      setError(true);
    } finally {
      setPending(false);
    }
  };
  return (
    <section aria-label="계좌 원문 열람">
      {!open ? (
        <Button variant="quiet" size="small" onClick={() => setOpen(true)}>
          계좌 원문 열람
        </Button>
      ) : (
        <>
          {account ? (
            <DetailList
              items={[
                {
                  label: "입금 계좌 원문",
                  value: `${account.bankName} · ${account.accountNumber}`,
                },
                { label: "예금주 원문", value: account.accountHolder },
              ]}
            />
          ) : (
            <>
              <Field
                label="계좌 열람 사유"
                control="textarea"
                value={reason}
                maxLength={2000}
                disabled={pending}
                onChange={(event) => setReason(event.currentTarget.value)}
              />
              <p>열람 사유와 계좌 조회 기록이 감사 이력에 남습니다.</p>
              <Button
                size="small"
                disabled={
                  reason.trim().length < 5 || reason.trim().length > 2000
                }
                isLoading={pending}
                onClick={() => {
                  void reveal();
                }}
              >
                열람 사유 제출
              </Button>
            </>
          )}
          {error ? (
            <Alert persistent variant="danger" title="계좌 열람 실패">
              열람 권한과 연결 상태를 확인한 뒤 다시 시도해 주세요.
            </Alert>
          ) : null}
          <Button
            variant="quiet"
            size="small"
            disabled={pending}
            onClick={() => {
              setAccount(undefined);
              setReason("");
              setError(false);
              setOpen(false);
            }}
          >
            계좌 원문 숨기기
          </Button>
        </>
      )}
    </section>
  );
}
